import { Box, Text, TextButton } from '@wix/design-system';
import { getSearchParams } from '@wix/package-picker-shared';
import {
  STUDIO_DARK_COLOR,
  STUDIO_LINK_COLOR,
  useIsStudioNewUi,
  useLocalization,
} from '@wix/package-picker-shared-studio';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import {
  CreativeOffer,
  PurchaseOffering,
} from '@wix/premium-purchase-platform/@sdk';
import { StatusAlertSmall } from '@wix/wix-ui-icons-common/odeditor';
import classNames from 'classnames';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import styles from './FeaturesGrid.scss';
import { FeaturesGridFeatureRowCell } from './FeaturesGridFeatureRowCell';
import { FeaturesGridNewBadge } from './FeaturesGridNewBadge';

const AI_CREDITS_TOOLTIP_KEY =
  'premium-purchase-plan.feature_table.ai_credits_section.ai_credits_tooltip';
const AI_CREDITS_DISCLAIMER_KEY =
  'premium-purchase-plan.package_picker.disclaimer.ai_credits_redesign';
const AI_CREDITS_URL = 'https://support.wix.com/article/about-ai-credits';
const AI_CREDITS_LINK_COLOR = 'var(--wds-color-text-primary)';

interface FeaturesGridFeatureRowProps {
  feature: CreativeOffer.GridFeature;
  products?: PurchaseOffering.PurchaseProduct[];
  recommendedProductId?: string;
}

export const FeaturesGridFeatureRow = ({
  feature,
  products,
  recommendedProductId,
}: FeaturesGridFeatureRowProps) => {
  const { t, Trans } = StudioPackagePickerModule.internals.useTranslations();
  const { domainName } = getSearchParams();
  const { shouldShowJapaneseExperience } = useLocalization();
  const isStudioNewUi = useIsStudioNewUi();
  const { experiments } =
    StudioPackagePickerModule.internals.experiments.hooks.useExperiments();

  if (!feature.content?.name?.translationKey || feature.metadata?.hidden) {
    return null;
  }

  const showNewBadge =
    experiments.isEnabled('ppNewFeaturesSep26') &&
    feature.metadata?.isNew === 'true';

  const defaultNameWeight = isStudioNewUi ? 'thin' : 'normal';

  const showAiCreditsDisclaimer =
    feature.content?.tooltip?.translationKey === AI_CREDITS_TOOLTIP_KEY;

  const translationValues = { domainName };
  const translationTags = {
    url: (
      <TextButton
        as="a"
        target="_blank"
        underline="onHover"
        size={shouldShowJapaneseExperience ? 'tiny' : 'small'}
        href={t(feature.metadata?.tooltipUrlKey ?? '')}
        onClick={() => {
          StudioPackagePickerModule.internals.bi.onFeatureTooltipLinkClick?.({
            featureKey: feature.content?.name?.translationKey,
            url: t(feature.metadata?.tooltipUrlKey ?? ''),
          });
        }}
      />
    ),
  };

  return (
    <PlatformFeaturesGrid.Content.FeatureRow.Root
      size={shouldShowJapaneseExperience ? 'medium' : 'small'}
    >
      <PlatformFeaturesGrid.Content.FeatureRow.Cell
        className={
          isStudioNewUi
            ? classNames(
                styles.featureRowCell,
                styles.featureNameCell,
              )
            : undefined
        }
      >
        <PlatformFeaturesGrid.Content.FeatureRow.Header
          nameWeight={shouldShowJapaneseExperience ? 'bold' : defaultNameWeight}
          name={
            <Trans
              i18nKey={feature.content?.name?.translationKey ?? ''}
              values={translationValues}
              components={translationTags}
            />
          }
          descriptionAsSubtitle={shouldShowJapaneseExperience}
          tooltip={
            <Text
              size="small"
              className={classNames(styles.featureRowDescription, {
                [styles.japan]: shouldShowJapaneseExperience,
              })}
              skin="standard"
              secondary={shouldShowJapaneseExperience}
            >
              <Trans
                i18nKey={feature.content?.tooltip?.translationKey ?? ''}
                values={translationValues}
                components={translationTags}
              />
              {showAiCreditsDisclaimer && (
                <>
                  <br />
                  <br />
                  <Text size="tiny" secondary>
                    <Trans
                      i18nKey={AI_CREDITS_DISCLAIMER_KEY}
                      components={{
                        1: (
                          // eslint-disable-next-line jsx-a11y/anchor-has-content -- content is injected by <Trans>
                          <a
                            href={AI_CREDITS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            ref={(el: HTMLAnchorElement | null) =>
                              el?.style.setProperty(
                                'color',
                                isStudioNewUi
                                  ? STUDIO_LINK_COLOR
                                  : AI_CREDITS_LINK_COLOR,
                                'important',
                              )
                            }
                          />
                        ),
                      }}
                    />
                  </Text>
                </>
              )}
            </Text>
          }
          badge={
            showNewBadge ? (
              <FeaturesGridNewBadge dataHook="features-grid-new-badge" />
            ) : undefined
          }
          tooltipIcon={
            isStudioNewUi ? (
              <span tabIndex={0} aria-label="More info">
                <Box>
                  <StatusAlertSmall color={STUDIO_DARK_COLOR} />
                </Box>
              </span>
            ) : undefined
          }
          onTooltipViewed={() => {
            StudioPackagePickerModule.internals.bi.onFeatureTooltipHover?.({
              featureKey: feature.content?.name?.translationKey,
            });
          }}
        />
      </PlatformFeaturesGrid.Content.FeatureRow.Cell>
      {products?.map((product) => (
        <FeaturesGridFeatureRowCell
          key={`${feature.id}-${product.productId}`}
          productId={product.productId ?? ''}
          feature={feature}
          isRecommended={product.productId === recommendedProductId}
        />
      ))}
    </PlatformFeaturesGrid.Content.FeatureRow.Root>
  );
};
